import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';

// Type definitions for preferences
export interface UserPreferences {
  [section: string]: {
    [key: string]: any;
  };
}

// Default preferences for different sections
export const DEFAULT_PREFERENCES = {
  assets: {
    viewMode: 'cards' as 'cards' | 'table',
    visibleColumns: [
      'name', 'type', 'asset_category', 'status', 'value_assessment', 'location', 'review_date', 'actions'
    ],
  },
  documents: {
    viewMode: 'cards' as 'cards' | 'table',
    sortBy: 'updated_at',
    sortOrder: 'desc' as 'asc' | 'desc',
  },
  trainings: {
    viewMode: 'cards' as 'cards' | 'table',
    showCompleted: true,
  }
};

interface PreferencesState {
  preferences: UserPreferences;
  isLoading: boolean;
  isInitialized: boolean;
  
  // Actions
  loadPreferences: () => Promise<void>;
  getPreference: <T = any>(section: string, key: string, defaultValue?: T) => T;
  setPreference: (section: string, key: string, value: any) => Promise<void>;
  setSectionPreferences: (section: string, preferences: Record<string, any>) => Promise<void>;
  deletePreference: (section: string, key: string) => Promise<void>;
  deleteSectionPreferences: (section: string) => Promise<void>;
  resetToDefaults: (section?: string) => Promise<void>;
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      preferences: {},
      isLoading: false,
      isInitialized: false,

      loadPreferences: async () => {
        console.log('Loading preferences from server...');
        set({ isLoading: true });
        try {
          const response = await api.getUserPreferences() as any;
          console.log('Server preferences response:', response);
          const serverPreferences = response?.data || {};
          
          // Simple merge with default preferences
          const mergedPreferences: UserPreferences = {
            ...DEFAULT_PREFERENCES,
            ...serverPreferences
          };

          console.log('Merged preferences:', mergedPreferences);
          set({ 
            preferences: mergedPreferences, 
            isLoading: false, 
            isInitialized: true 
          });
        } catch (error) {
          console.error('Failed to load preferences from server:', error);
          // Fall back to default preferences if server fails
          set({ 
            preferences: { ...DEFAULT_PREFERENCES }, 
            isLoading: false, 
            isInitialized: true 
          });
        }
      },

      getPreference: <T = any>(section: string, key: string, defaultValue?: T): T => {
        const { preferences } = get();
        const sectionPrefs = preferences[section];
        
        if (sectionPrefs && sectionPrefs[key] !== undefined) {
          return sectionPrefs[key];
        }
        
        // Check default preferences
        const defaultSectionPrefs = (DEFAULT_PREFERENCES as any)[section];
        if (defaultSectionPrefs && defaultSectionPrefs[key] !== undefined) {
          return defaultSectionPrefs[key];
        }
        
        return defaultValue as T;
      },

      setPreference: async (section: string, key: string, value: any) => {
        console.log('Setting preference:', { section, key, value });
        // Update local state immediately for better UX
        set(state => ({
          preferences: {
            ...state.preferences,
            [section]: {
              ...state.preferences[section],
              [key]: value
            }
          }
        }));

        // Sync to server in background
        try {
          console.log('Syncing preference to server...');
          await api.setSinglePreference(section, key, value);
          console.log('Preference synced successfully');
        } catch (error) {
          console.error('Failed to save preference to server:', error);
          // Could implement retry logic or show notification here
        }
      },

      setSectionPreferences: async (section: string, preferences: Record<string, any>) => {
        // Update local state immediately
        set(state => ({
          preferences: {
            ...state.preferences,
            [section]: {
              ...state.preferences[section],
              ...preferences
            }
          }
        }));

        // Sync to server in background
        try {
          await api.setSectionPreferences(section, preferences);
        } catch (error) {
          console.error('Failed to save section preferences to server:', error);
        }
      },

      deletePreference: async (section: string, key: string) => {
        // Update local state immediately
        set(state => {
          const newPreferences = { ...state.preferences };
          if (newPreferences[section]) {
            const newSectionPrefs = { ...newPreferences[section] };
            delete newSectionPrefs[key];
            newPreferences[section] = newSectionPrefs;
          }
          return { preferences: newPreferences };
        });

        // Sync to server in background
        try {
          await api.deleteSinglePreference(section, key);
        } catch (error) {
          console.error('Failed to delete preference from server:', error);
        }
      },

      deleteSectionPreferences: async (section: string) => {
        // Update local state immediately
        set(state => {
          const newPreferences = { ...state.preferences };
          delete newPreferences[section];
          return { preferences: newPreferences };
        });

        // Sync to server in background
        try {
          await api.deleteSectionPreferences(section);
        } catch (error) {
          console.error('Failed to delete section preferences from server:', error);
        }
      },

      resetToDefaults: async (section?: string) => {
        if (section) {
          // Reset specific section
          const defaultSectionPrefs = (DEFAULT_PREFERENCES as any)[section];
          if (defaultSectionPrefs) {
            await get().setSectionPreferences(section, defaultSectionPrefs);
          }
        } else {
          // Reset all preferences
          set({ preferences: { ...DEFAULT_PREFERENCES } });
          try {
            await api.deleteAllPreferences();
            // Re-sync defaults to server
            for (const [sectionKey, sectionPrefs] of Object.entries(DEFAULT_PREFERENCES)) {
              await api.setSectionPreferences(sectionKey, sectionPrefs);
            }
          } catch (error) {
            console.error('Failed to reset preferences on server:', error);
          }
        }
      },
    }),
    {
      name: 'user-preferences',
      partialize: (state) => ({
        preferences: state.preferences,
        isInitialized: state.isInitialized,
      }),
    }
  )
);
